import Bugsnag from '@bugsnag/js';
import { useFeatureFlagPayload } from '@posthog/react';
import type { JsonType } from 'posthog-js';
import { useEffect, useMemo, useState } from 'react';
import { isFeatureFlagEnabled } from '~/common/posthog';
import { useOrgResolvedFlag } from '~/hooks/useOrgResolvedFlag';
import { july2026Update } from './announcementContent/LayerAttributesJuly2026';
import { september2026Update } from './announcementContent/LayerLibrarySept2026';
import { FeatureAnnouncementModal } from './FeatureAnnouncementModal';
import { type FeatureAnnouncementContent, isFeatureAnnouncementContent } from './types';

export const S3_PRODUCT_UPDATE_FLAG = 's3-product-updates';

export const PRODUCT_UPDATE_DIALOG_TEST_ID_PREFIX = 'product-update-dialog';
/** Shared by PageLayout and LayerAttributesAnnouncementPage so their testids stay in sync. */
export const PAGE_1_TEST_ID_PREFIX = `${PRODUCT_UPDATE_DIALOG_TEST_ID_PREFIX}-page-1`;

function FeatureItem({ title, description, testId }: { title: string; description: string; testId: string }) {
  return (
    <div className="space-y-1" data-testid={testId}>
      <p className="font-semibold" data-testid={`${testId}-title`}>
        {title}
      </p>
      <p
        data-testid={`${testId}-body`}
        /* biome-ignore lint/security/noDangerouslySetInnerHtml: need to do this to support styling and links */
        dangerouslySetInnerHTML={{ __html: description }}
        className={`[&>a]:text-blue-50 [&>a]:underline [&>a]:hover:text-blue-60`}
      />
    </div>
  );
}

function PageLayout({
  featureTitle,
  featureDescription,
  announcementSubtitle,
  image,
  featureItems,
}: FeatureAnnouncementContent) {
  const p = PAGE_1_TEST_ID_PREFIX;
  return (
    <div className="flex gap-4" data-testid={`${p}-layout`}>
      <div
        className="flex w-90 shrink-0 flex-col gap-3 text-sm leading-[140%] text-black"
        data-testid={`${p}-text-column`}
      >
        <p data-testid={`${p}-subtitle`}>{announcementSubtitle}</p>
        <h3 className="text-base font-semibold" data-testid={`${p}-section-title`}>
          {featureTitle}
        </h3>
        <div
          data-testid={`${p}-description`}
          /* biome-ignore lint/security/noDangerouslySetInnerHtml: need to do this to support styling and links */
          dangerouslySetInnerHTML={{ __html: featureDescription }}
          className={`[&>a]:text-blue-50 [&>a]:underline [&>a]:hover:text-blue-60`}
        />
        <div className="flex flex-col gap-3" data-testid={`${p}-features`}>
          {featureItems.map((item) => {
            return (
              <FeatureItem
                title={item.title}
                key={item.title}
                testId={`${PAGE_1_TEST_ID_PREFIX}-feature-${item.testId}`}
                description={item.description}
              />
            );
          })}
        </div>
      </div>
      <div className="min-w-0 flex-1 flex justify-center" data-testid={`${p}-media-column`}>
        <div data-testid={`${p}-image-frame`}>
          <img src={image.src} alt={image.alt} loading="eager" decoding="async" data-testid={`${p}-screenshot`} />
        </div>
      </div>
    </div>
  );
}

/** Gets and validates the feature annoucment conent from the given url */
export async function getJsonFromUrl(url: string): Promise<FeatureAnnouncementContent> {
  if (!isProductUpdateUrlValid(url)) {
    throw new Error(`Invalid product update url: ${url}`);
  }
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Error when fetching JSON for product update at url "${url}". HTTP status: ${response.status}`);
  }
  const jsonData = await response.json();
  if (isFeatureAnnouncementContent(jsonData)) {
    return jsonData;
  } else {
    throw new Error(
      `Error when fetching JSON for product update at url "${url}". Data does not fit the FeatureAnnouncementContent format.`,
    );
  }
}

/** Locally-hosted update content only used when S3_PRODUCT_UPDATE_FLAG is off */
const nonS3Updates = [september2026Update, july2026Update]; // hardcoded order from newest to oldest

/** Get the product update content to show, using the locally hosted update content */
export const getNonS3UpdateContent = (
  orderedUpdatesList: FeatureAnnouncementContent[] = nonS3Updates,
): FeatureAnnouncementContent => {
  return (
    orderedUpdatesList.find((update) => {
      if (!update.featureFlag) return true;
      return isFeatureFlagEnabled(update.featureFlag);
    }) ?? orderedUpdatesList[orderedUpdatesList.length - 1] // if no newer updates are available, show the oldest one and ignore its feature flag
  );
};

/**
 * Compute the full url for the update's image.
 * The image src stored in s3 is just the img file name to keep s3 updates simple.
 * We compute the full path here, assuming that the image file lives right next to the json.
 */
export const getS3ImageUrl = (s3JsonUrl: string, imgFileName: string): string => {
  if (!isProductUpdateUrlValid(s3JsonUrl)) {
    throw new Error(`Invalid product update url: ${s3JsonUrl}`);
  }
  const imgPathParts = s3JsonUrl.split('/').slice(0, -1);
  imgPathParts.push(imgFileName);
  return imgPathParts.join('/');
};

/** Return true if the posthog flag payload is a valid url pointing to a json file in our bucket. */
export const isProductUpdateUrlValid = (updateToShow: JsonType): updateToShow is string => {
  if (typeof updateToShow !== 'string') return false;

  try {
    const url = new URL(updateToShow);
    return url.origin === 'https://cg-product-updates.s3.us-east-2.amazonaws.com' && url.pathname.endsWith('.json');
  } catch {
    return false;
  }
};

export function FeatureAnnouncement({
  open,
  onClose,
  onCloseAutoFocus,
}: {
  open?: boolean;
  onClose?: () => void;
  onCloseAutoFocus?: (event: Event) => void;
} = {}) {
  const { resolved: areFlagsResolved, enabled: useS3Updates } = useOrgResolvedFlag(S3_PRODUCT_UPDATE_FLAG);
  const updateToShow = useFeatureFlagPayload(S3_PRODUCT_UPDATE_FLAG);
  const [updateContent, setUpdateContent] = useState<FeatureAnnouncementContent | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [areAnnouncementImagesPreloaded, setAreAnnouncementImagesPreloaded] = useState(false);

  const nonS3UpdateContent = useMemo(() => getNonS3UpdateContent(), []);

  useEffect(() => {
    setIsLoading(true);

    if (!areFlagsResolved) {
      setUpdateContent(undefined); // Unset content if flags aren't resolved, we don't want to show the wrong update unintentially
      setIsLoading(false);
      return;
    }
    if (useS3Updates) {
      if (!isProductUpdateUrlValid(updateToShow)) {
        Bugsnag.notify(new Error(`Invalid product update url in "${S3_PRODUCT_UPDATE_FLAG}" payload: ${updateToShow}`));
        setUpdateContent(undefined);
        setIsLoading(false);
        return;
      }

      const fetchContentJSON = async () => {
        try {
          const contentJSON: FeatureAnnouncementContent = await getJsonFromUrl(updateToShow);
          if (!(contentJSON.featureFlag && !isFeatureFlagEnabled(contentJSON.featureFlag))) {
            setUpdateContent({
              ...contentJSON,
              image: { ...contentJSON.image, src: getS3ImageUrl(updateToShow, contentJSON.image.src) },
            });
          }
        } catch (e) {
          Bugsnag.notify(
            new Error(`Unable to fetch product update content from "${updateToShow}": ${String(e)}`, { cause: e }),
          );
          setUpdateContent(undefined);
        } finally {
          setIsLoading(false);
        }
      };
      fetchContentJSON();
    } else {
      setUpdateContent(nonS3UpdateContent);
      setIsLoading(false);
    }
  }, [updateToShow, areFlagsResolved, useS3Updates, nonS3UpdateContent]);

  useEffect(() => {
    if (areAnnouncementImagesPreloaded || typeof updateContent === 'undefined') {
      return;
    }

    setAreAnnouncementImagesPreloaded(true);
    try {
      const img = new Image();
      img.src = updateContent.image.src;
      img.decoding = 'async';
      // Decode early when possible so first render can paint from cache.
      img.decode().catch((e) => {
        Bugsnag.notify(
          new Error(`Error decoding product update image from url "${updateContent.image.src}": ${e.message}`, {
            cause: e,
          }),
        );
      });
    } catch (e) {
      Bugsnag.notify(
        new Error(`Error decoding product update image from url "${updateContent.image.src}"`, {
          cause: e,
        }),
      );
    }
  }, [updateContent, areAnnouncementImagesPreloaded]);

  return (
    <FeatureAnnouncementModal
      announcementId={updateContent?.announcementId}
      title={updateContent?.announcementTitle}
      pages={
        typeof updateContent !== 'undefined'
          ? [
              {
                key: 'pageOne',
                content: <PageLayout {...updateContent} />,
              },
            ]
          : []
      }
      isLoading={isLoading}
      open={open}
      onClose={onClose}
      onCloseAutoFocus={onCloseAutoFocus}
      dataTestIdPrefix={PRODUCT_UPDATE_DIALOG_TEST_ID_PREFIX}
    />
  );
}
